import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Plane, AlertOctagon, ClipboardList, Package } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * "My Office" tile — compact per-city status snapshot on the home dashboard.
 *
 * - PCWM / GEHM / SMCM : shows their assigned Destination + City stats.
 * - FOUNDER / BDOM     : shows "All offices" totals (they see everything).
 *
 * Non-Founder roles that haven't been assigned a location are shown a soft
 * empty state instead of raw zeros — the Founder needs to set their office.
 */
export default function MyOfficeTile() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/my-office/summary")
      .then(({ data }) => { if (mounted) setData(data); })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const role = user?.effective_role;
  const isAdmin = role === "FOUNDER" || role === "BDOM";
  const unassigned = !isAdmin && !user?.destination && !user?.city;

  const items = [
    {
      key: "arrivals_this_month",
      label: "Arrivals this month",
      icon: Plane,
      href: "/arrivals",
      tone: "text-sky-700 bg-sky-50 border-sky-200",
      testid: "myoffice-arrivals",
    },
    {
      key: "open_issues",
      label: "Open issues (30d)",
      icon: AlertOctagon,
      href: "/issues",
      tone: "text-rose-700 bg-rose-50 border-rose-200",
      testid: "myoffice-issues",
    },
    {
      key: "unfilled_placements",
      label: "Unfilled placements",
      icon: ClipboardList,
      href: "/program-bookings",
      tone: "text-amber-700 bg-amber-50 border-amber-200",
      testid: "myoffice-placements",
    },
    {
      key: "inventory_alerts",
      label: "Inventory alerts",
      icon: Package,
      href: "/inventory",
      tone: "text-violet-700 bg-violet-50 border-violet-200",
      testid: "myoffice-inventory",
    },
  ];

  return (
    <div className="aii-card p-5" data-testid="myoffice-tile">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1">
            <MapPin size={11} /> {isAdmin ? "Team overview" : "My office"}
          </div>
          <h3 className="font-display font-semibold text-lg mt-0.5" data-testid="myoffice-location">
            {loading ? "…" : (data?.location || "—")}
          </h3>
        </div>
        {isAdmin && (
          <span className="text-[11px] text-[color:var(--aii-muted)]">
            Aggregated across every destination. Filter individual pages to slice by city.
          </span>
        )}
      </div>

      {unassigned ? (
        <div className="text-sm text-[color:var(--aii-muted)] py-4 text-center border border-dashed border-[color:var(--aii-border)] rounded-lg" data-testid="myoffice-empty">
          You don&apos;t have a Destination + City assigned yet. Ask the Founder to set it on the Team page.
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {items.map(({ key, label, icon: Icon, href, tone, testid }) => (
            <Link
              to={href}
              key={key}
              data-testid={testid}
              className={`border rounded-lg p-3 transition hover:shadow-sm ${tone}`}
            >
              <div className="flex items-center justify-between">
                <Icon size={14} />
                <span className="text-[10px] uppercase tracking-widest opacity-70">Live</span>
              </div>
              <div className="text-2xl font-display font-semibold mt-1.5">
                {loading ? "…" : (data?.[key] ?? 0)}
              </div>
              <div className="text-xs mt-0.5 opacity-90">{label}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
